import { zodResolver } from '@hookform/resolvers/zod';
import { Image as ImageIcon, Loader2, Palette, Save, Upload } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getGetTenantQueryKey, useGetTenant, useUpdateTenant } from '@/api/generated/tenants';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';
import { useToast } from '@/hooks/use-toast';

const brandingSettingsSchema = z.object({
  logoUrl: z.string().optional(),
  bannerUrl: z.string().optional(),
  config: z
    .object({
      theme: z
        .object({
          primary: z.string().optional(),
          secondary: z.string().optional(),
          accent: z.string().optional(),
        })
        .optional(),
    })
    .passthrough(),
});

type BrandingSettingsFormValues = z.infer<typeof brandingSettingsSchema>;

export default function BrandingSettings() {
  const { t } = useTranslation();
  const { selectedTenant, refreshTenant } = useTenant();
  const { toast } = useToast();

  const form = useForm<BrandingSettingsFormValues>({
    resolver: zodResolver(brandingSettingsSchema),
    defaultValues: {
      logoUrl: '',
      bannerUrl: '',
      config: {
        theme: {
          primary: '#2563EB',
          secondary: '#1E293B',
          accent: '#F59E0B',
        },
      },
    },
  });

  const { data: result, isLoading: loading } = useGetTenant(selectedTenant?.id || '', {
    query: {
      enabled: !!selectedTenant?.id,
      queryKey: getGetTenantQueryKey(selectedTenant?.id || ''),
    },
  });

  useEffect(() => {
    if (result?.data) {
      const data = result.data;
      form.reset({
        logoUrl: data.logoUrl || '',
        bannerUrl: data.bannerUrl || '',
        config: {
          ...data.config,
          theme: {
            primary: data.config?.theme?.primary || '#2563EB',
            secondary: data.config?.theme?.secondary || '#1E293B',
            accent: data.config?.theme?.accent || '#F59E0B',
          },
        },
      });
    }
  }, [result, form]);

  const updateMutation = useUpdateTenant();

  const onSubmit = async (data: BrandingSettingsFormValues) => {
    if (!selectedTenant) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedTenant.id,
        data,
      });

      toast({ title: 'Success', description: 'Branding settings updated.' });
      if (refreshTenant) refreshTenant();
    } catch (error) {
      console.error(error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Update failed',
      });
    }
  };

  if (!selectedTenant) return <div>Select a tenant</div>;
  if (loading) return <div>Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <header className="flex items-center justify-between pb-4 border-b">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('menu.branding')}</h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage university branding assets, colors, and visual identity.
              </p>
            </div>
            <Button type="submit" disabled={form.formState.isSubmitting || loading}>
              {form.formState.isSubmitting || loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {t('common.save')}
            </Button>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
            {/* Logo Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-2 border-b border-gray-100">
                <Upload className="w-5 h-5 text-gray-400" />
                Logos & Icons
              </h3>

              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo Image URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormDescription>URL to the university logo (Header).</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Theme Colors */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-2 border-b border-gray-100">
                <Palette className="w-5 h-5 text-gray-400" />
                Theme & Colors
              </h3>

              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="config.theme.primary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Color</FormLabel>
                        <div className="flex gap-2">
                          <Input type="color" className="w-12 p-1 h-10" {...field} />
                          <Input {...field} />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="config.theme.secondary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Color</FormLabel>
                        <div className="flex gap-2">
                          <Input type="color" className="w-12 p-1 h-10" {...field} />
                          <Input {...field} />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="config.theme.accent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Accent Color</FormLabel>
                        <div className="flex gap-2">
                          <Input type="color" className="w-12 p-1 h-10" {...field} />
                          <Input {...field} />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Hero Images */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-2 border-b border-gray-100">
                <ImageIcon className="w-5 h-5 text-gray-400" />
                Portal Images
              </h3>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="bannerUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Applicant Portal Banner URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormDescription>Hero image for the top page (1200x400).</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
